import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, Share2, MapPin, Package, Shield, Truck, ShoppingBag, MessageSquare, ArrowLeft, ChevronLeft, ChevronRight, X, Plus, Minus, Pencil, Wand2, Check } from 'lucide-react';

import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import MetaTags from '@/components/MetaTags';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ImageManager } from '@/lib/imageManager';
import { isPackageBasedProduct, calculatePackageMetrics, formatPackageDisplay, formatPackageCoverage, validatePackageQuantity, getPackageBaseUnit } from '@/lib/packageMeasurementUtils';
import { GroupBuyTooltipBadge } from '@/components/groupbuy/GroupBuyTooltipBadge';
import { getPublicSiteUrl } from '@/lib/siteConfig';

import ProductGradeBadge from '@/components/ProductGradeBadge';
import GradeLegend from '@/components/GradeLegend';
import { SimpleGroupBuyDisplay } from '@/components/groupbuy/SimpleGroupBuyDisplay';
import { LiquidationSaleDisplay } from '@/components/liquidation/LiquidationSaleDisplay';
import { LiquidationBadge } from '@/components/liquidation/LiquidationBadge';
import SimilarProducts from '@/components/SimilarProducts';
import { ProductActions } from '@/components/product/ProductActions';
import { formatPrice, formatMeasurementUnit, formatDimensionUnit } from '@/lib/formatters';
import { useGradeSettings } from '@/hooks/useGradeSettings';
import { useRecentProducts } from '@/hooks/useRecentProducts';
import { trackAdViewContent } from '@/lib/analytics';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, items, openCart, updateQuantity, removeItem } = useCart();
  const { user, profile, isAdmin } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    requested_quantity: '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    message: ''
  });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [userQuantity, setUserQuantity] = useState<string>('');
  const [safetyMargin, setSafetyMargin] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [flashQuoteButton, setFlashQuoteButton] = useState(false);
  const [showAiTooltip, setShowAiTooltip] = useState(false);
  const { getGradeSettings } = useGradeSettings();
  const { addProduct } = useRecentProducts();

  // Update quote form when profile changes
  useEffect(() => {
    if (profile) {
      setQuoteFormData(prev => ({
        ...prev,
        email: profile.email || '',
        phone: profile.phone || ''
      }));
    }
  }, [profile]);

  // Fetch product data from database
  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;

      try {
        // console.log('Fetching product with slug:', slug);

        // React Router usually decodes, but ensure it's clean and normalized
        const rawSlug = slug;
        const decodedSlug = decodeURIComponent(rawSlug).normalize('NFC');

        // console.log('Fetching product:', { raw: rawSlug, decoded: decodedSlug });

        // Try to fetch by slug using the secure function
        let { data, error } = await supabase
          .rpc('get_product_details_by_slug', { product_slug: decodedSlug });

        // console.log('Slug RPC response:', { data, error, dataLength: data?.length });

        // If error OR no data returned from RPC, try direct table query as fallback
        if (error || !data || data.length === 0) {
          // console.log('RPC failed, trying direct query');

          // Try exact match first
          let directResult = await supabase
            .from('products')
            .select('*')
            .eq('slug', decodedSlug)
            .eq('is_active', true)
            .maybeSingle();

          // If exact match fails, try case-insensitive
          if (!directResult.data) {
            // console.log('Exact match failed, trying ilike');
            directResult = await supabase
              .from('products')
              .select('*')
              .ilike('slug', decodedSlug)
              .eq('is_active', true)
              .maybeSingle();
          }

          if (directResult.data) {
            data = [directResult.data] as any;
            error = directResult.error;
            // console.log('Direct query result:', { data, error });
          }
        }

        // If not found by slug, try by ID if the slug looks like a UUID
        if ((!data || data.length === 0) && error?.code !== 'PGRST116') {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(decodedSlug)) {
            // console.log('Trying to fetch by ID:', decodedSlug);
            const result = await supabase
              .rpc('get_product_details_by_id', { product_id: decodedSlug });
            data = result.data as any;
            error = result.error;
            // console.log('ID RPC response:', { data, error });
          }
        }

        // Transform data to include category information
        if (data && data.length > 0) {
          const productData = data[0];

          // Fetch category and subcategory names
          const [categoryResult, subcategoryResult] = await Promise.all([
            productData.category_id
              ? supabase.from('categories').select('name').eq('id', productData.category_id).maybeSingle()
              : { data: null },
            productData.subcategory_id
              ? supabase.from('subcategories').select('name').eq('id', productData.subcategory_id).maybeSingle()
              : { data: null }
          ]);

          // Create enhanced product data with category information
          const enhancedProductData = {
            ...productData,
            categories: categoryResult.data ? { name: categoryResult.data.name } : null,
            subcategories: subcategoryResult.data ? { name: subcategoryResult.data.name } : null
          };

          // Extra security check: if product is inactive, only allow admins to view it
          if (!enhancedProductData.is_active && !isAdmin) {
            // console.log('Product is inactive and user is not admin, hiding');
            setProduct(null);
          } else {
            setProduct(enhancedProductData);
            setQuantity(enhancedProductData.min_quantity || 1);
          }
        } else if (!data || data.length === 0) {
          // console.log('Product not found with slug:', decodedSlug);
          setProduct(null);
        }

        if (error) {
          console.error('Error fetching product:', error);
          toast.error("שגיאה", {
            description: "שגיאה בטעינת המוצר"
          });
          return;
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error("שגיאה", {
          description: "שגיאה בטעינת המוצר"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  /* useEffect(() => {
    if (product) {
       console.log('Liquidation Data:', {
        enabled: product.liquidation_sale_enabled,
        total: product.liquidation_total_inventory,
        current: product.liquidation_current_inventory,
        threshold: product.liquidation_threshold_percent
      }); 
    }
  }, [product]); */



  // Track product view and add to recent products
  useEffect(() => {
    if (product) {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'view_item', {
          currency: 'ILS',
          value: product.price_per_unit || 0,
          items: [{
            item_id: product.id?.toString() || '',
            item_name: product.name || '',
            item_category: product.categories?.name || 'Uncategorized',
            item_category2: product.subcategories?.name,
            price: product.price_per_unit || 0,
            quantity: 1
          }]
        });
      }
      
      // Track ad pixel ViewContent
      trackAdViewContent(product.price_per_unit || 0, product.id?.toString() || '');

      // Add to recently viewed products
      addProduct({
        id: product.id.toString(),
        name: product.name,
        price_per_unit: product.price_per_unit,
        images: product.images || [],
        slug: product.slug || '',
        category_name: product.categories?.name
      });
    }
  }, [product, addProduct]);

  // Check if product is already in cart
  const cartItem = product ? items.find(item => item.id === product.id?.toString()) : null;
  const isInCart = !!cartItem;

  // Package-based calculation
  const isPackageMode = isPackageBasedProduct(product?.measurement_unit || '');
  const packageMetrics = useMemo(() => {
    if (!product || !isPackageMode) return null;
    return calculatePackageMetrics(
      {
        measurement_unit: product.measurement_unit,
        price_per_unit: product.price_per_unit,
        package_coverage_per_unit: product.package_coverage_per_unit,
        minimum_packages: product.minimum_packages,
        min_quantity: product.min_quantity || 1,
        max_quantity: product.max_quantity,
        step_quantity: product.step_quantity || 1,
      },
      quantity
    );
  }, [product, isPackageMode, quantity]);

  // Check if product is currently on sale (within sale period)
  const isOnSale = useMemo(() => {
    if (!product?.sale_start || !product?.sale_end) return false;
    const now = new Date();
    const saleStart = new Date(product.sale_start);
    const saleEnd = new Date(product.sale_end);
    return now >= saleStart && now <= saleEnd;
  }, [product?.sale_start, product?.sale_end]);

  // Calculate days remaining in sale
  const daysRemaining = useMemo(() => {
    if (!isOnSale || !product?.sale_end) return null;
    const now = new Date();
    const saleEnd = new Date(product.sale_end);
    const diff = saleEnd.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  }, [isOnSale, product?.sale_end]);

  // Initialize userQuantity when product loads
  useEffect(() => {
    if (product) {
      if (isPackageBasedProduct(product.measurement_unit) && product.package_coverage_per_unit) {
        // For package products, userQuantity IS packages
        // Start with min_quantity (NOW IN PACKAGES)
        const minPackages = product.min_quantity || 1;
        setUserQuantity(minPackages.toString());
        setQuantity(minPackages);
      } else {
        const initialQty = product.min_quantity || 1;
        setUserQuantity(initialQty.toString());
        setQuantity(initialQty);
      }
    }
  }, [product]);

  // Sync quantity with cart item when cart changes
  useEffect(() => {
    if (product && cartItem) {
      setQuantity(cartItem.quantity);
      // Update userQuantity based on cart quantity
      if (isPackageBasedProduct(product.measurement_unit) && product.package_coverage_per_unit) {
        // userQuantity should match package count
        setUserQuantity(cartItem.quantity.toString());
      } else {
        setUserQuantity(cartItem.quantity.toString());
      }
    }
  }, [cartItem?.quantity, product]);

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate requested quantity is greater than max allowed
    const requestedQty = parseInt(quoteFormData.requested_quantity);
    const minRequired = (product.max_quantity || product.total_inventory) + 1;

    if (isNaN(requestedQty) || requestedQty < minRequired) {
      toast.error("כמות לא תקינה", {
        description: `אנא הזן כמות גדולה מ-${product.max_quantity || product.total_inventory}`
      });
      return;
    }

    // Import validation functions
    const { validateQuoteRequest, sanitizeInput, sanitizeHtml } = await import('@/lib/security');

    // Validate the form data
    const validationResult = validateQuoteRequest({
      ...quoteFormData,
      requested_quantity: requestedQty
    });

    if (!validationResult.isValid) {
      toast.error("שגיאה בבדיקת הנתונים", {
        description: validationResult.errors[0]
      });
      return;
    }

    // Check rate limiting before submission
    const rateLimitOk = await supabase.rpc('check_request_rate_limit', {
      email_param: quoteFormData.email,
      request_type: 'quote_request'
    });

    if (rateLimitOk.error || !rateLimitOk.data) {
      toast.error("חרגת ממגבלת הבקשות", {
        description: "יותר מדי בקשות הערכת מחיר מאותה כתובת מייל. אנא נסה שוב בעוד שעה."
      });
      setQuoteLoading(false);
      return;
    }

    setQuoteLoading(true);
    try {
      const { error } = await supabase
        .from('quote_requests')
        .insert({
          product_id: product.id,
          product_name: product.name,
          product_description: product.description,
          user_id: user?.id || null,
          requested_quantity: parseInt(quoteFormData.requested_quantity),
          email: sanitizeInput(quoteFormData.email),
          phone: sanitizeInput(quoteFormData.phone),
          message: sanitizeHtml(quoteFormData.message || ''),
          status: 'pending'
        });

      if (error) throw error;

      toast.success("הבקשה נשלחה בהצלחה!", {
        description: "בקשת הצעת המחיר נשלחה בהצלחה! נחזור אליך בהקרוב"
      });
      setShowQuoteForm(false);
      setQuoteFormData({
        requested_quantity: '',
        email: profile?.email || '',
        phone: profile?.phone || '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error("שגיאה", {
        description: "שגיאה בשליחת הבקשה"
      });
    } finally {
      setQuoteLoading(false);
    }
  };




  const handleAddToCart = () => {
    // Use the component-level useMemo check for consistency
    const unitPriceForCart = isPackageMode
      ? product.price_per_unit * (product.package_coverage_per_unit || 1)
      : product.price_per_unit;

    const productListing = {
      id: product.id.toString(),
      name: product.name,
      price: unitPriceForCart,
      originalPrice: unitPriceForCart,
      image: ImageManager.getImageUrl(product.images?.[0] || ''),
      supplier: product.supplier_name || product.city,
      location: product.city,
      availableQuantity: product.total_inventory,
      slug: slug,
      group_buy_enabled: !!(product.group_buy_enabled || product.group_buy_campaign_id),
      liquidation_sale_enabled: !!(product.liquidation_sale_enabled || product.is_liquidation_sale),
      grade: product.grade,
    };

    // For package products, pass the requestedQuantity (total SQM) based on packages * coverage
    // userQuantity is now PACKAGES
    const requestedQty = isPackageMode && product.package_coverage_per_unit
      ? (parseFloat(userQuantity) || 0) * product.package_coverage_per_unit
      : undefined;

    addItem(
      productListing,
      quantity,
      product.min_quantity,
      product.step_quantity,
      product.max_quantity,
      isPackageMode,
      product.package_coverage_per_unit,
      product.measurement_unit,
      requestedQty,
      isInCart // Overwrite if already in cart
    );
  };

  // Combine standard images and simulation images for the gallery
  const allImages = useMemo(() => {
    if (!product) return [];
    const images = product.images || [];
    const simulations = product.simulation_images || [];
    return [...images, ...simulations];
  }, [product?.images, product?.simulation_images]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background" dir="rtl">
        <Navigation />
        <main className="pt-8 lg:pt-8 pb-12">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>טוען מוצר...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir="rtl">
        <Navigation />
        <main className="pt-20 lg:pt-8 pb-12">
          <div className="container mx-auto px-4 text-center">
            <Package className="h-24 w-24 text-muted-foreground mx-auto mb-6 mt-8" />
            <h1 className="text-2xl font-bold mb-4">מוצר לא נמצא</h1>
            <p className="text-muted-foreground mb-6">המוצר שחיפשת אינו קיים או אינו במלאי</p>
            <Button onClick={() => navigate('/products')}>חזור לרשימת המוצרים</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Determine if this is a group buy product
  const isGroupBuy = product.group_buy_enabled || product.group_buy_campaign_id;

  return (
    <div className="min-h-screen bg-background">
      <MetaTags
        title={`${product.name} - Excess Market`}
        description={product.description || `${product.name} במחיר מיוחד ב-Excess Market. ${product.location ? `זמין ב${product.location}` : ''}`}
        image={product.images?.[0] ? `https://voyrsahkomcidcwjxcst.supabase.co/storage/v1/object/public/product-images/${product.images[0]}` : "/uploads/7abb424c-448f-4489-8b45-04aa70154302.png"}
        url={`${getPublicSiteUrl()}/product/${product.slug}`}
        type="product"
      />
      <Navigation />

      <main className="pt-4 lg:pt-8 pb-12">
        <div className="container mx-auto px-4">
          {!product.is_active && isAdmin && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 text-amber-800" dir="rtl">
              <Shield className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-bold text-sm md:text-base">מצב תצוגה מקדימה לאדמין</p>
                <p className="text-xs md:text-sm">מוצר זה מוגדר כלא פעיל ואינו גלוי ללקוחות. רק מנהלי מערכת יכולים לצפות בעמוד זה.</p>
              </div>
            </div>
          )}
          {/* Breadcrumbs with Admin Edit Button */}
          <div className="mb-4 mt-0 flex items-center justify-between" dir="rtl">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    בית
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronLeft className="h-4 w-4" />
                </BreadcrumbSeparator>
                {product.categories?.name && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        href={`/category/${encodeURIComponent(product.categories.name)}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {product.categories.name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator>
                      <ChevronLeft className="h-4 w-4" />
                    </BreadcrumbSeparator>
                  </>
                )}
                {product.subcategories?.name && product.subcategories.name !== 'ללא תת-קטגוריה' && (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        href={`/category/${encodeURIComponent(product.categories?.name || '')}?sub=${encodeURIComponent(product.subcategories.name)}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {product.subcategories.name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator>
                      <ChevronLeft className="h-4 w-4" />
                    </BreadcrumbSeparator>
                  </>
                )}
                <BreadcrumbItem>
                  <BreadcrumbPage>{product.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Admin Edit Button */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => window.open(`/admin/product/edit/${product.id}`, '_blank')}
              >
                <Pencil className="h-4 w-4" />
                עריכת מוצר
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Images */}
            <div>
              <div className="relative mb-4 cursor-pointer" onClick={() => {
                setLightboxImageIndex(currentImageIndex);
                setLightboxOpen(true);
              }}>
                <div className="aspect-square w-full overflow-hidden rounded-lg bg-muted relative">
                  {/* Grade Ribbon */}
                  {product.grade && (
                    <div
                      className={`absolute top-5 -right-12 w-40 rotate-45 py-1 text-center font-bold shadow-lg z-10 text-sm border-2 uppercase tracking-wider cursor-pointer hover:scale-105 transition-transform
                        ${product.grade === 'A' ? 'bg-emerald-50 text-emerald-600 border-emerald-200 shadow-emerald-500/10' :
                          product.grade === 'B' ? 'bg-blue-50 text-blue-600 border-blue-200 shadow-blue-500/10' :
                            product.grade === 'C' ? 'bg-amber-50 text-amber-600 border-amber-200 shadow-amber-500/10' :
                              'bg-rose-50 text-rose-600 border-rose-200 shadow-rose-500/10'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const element = document.getElementById('grade-legend-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                    >
                      דירוג {product.grade}
                    </div>
                  )}
                  <img
                    src={ImageManager.getImageUrl(allImages[currentImageIndex] || '')}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={(e) => ImageManager.handleImageError(e)}
                  />

                  {/* AI Simulation Badge */}
                  {currentImageIndex >= (product.images?.length || 0) && (
                    <div className="absolute bottom-3 right-3 z-20 group/ai">
                      <div className="bg-purple-600/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-md cursor-help">
                        <Wand2 className="w-3 h-3" />
                        <span className="font-medium">AI</span>
                      </div>
                      <div className="absolute bottom-full right-0 mb-2 hidden group-hover/ai:block w-64 bg-purple-900 text-white text-xs p-3 rounded-lg shadow-xl z-30">
                        <p className="leading-relaxed">
                          תמונה זו נוצרה באמצעות בינה מלאכותית להמחשה בלבד. המרקם והגוון המדויק עשויים להיות שונים במציאות.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons (Share & Favorite) */}
                  <div className="absolute bottom-2 left-2 flex gap-2 z-10">
                    <Button
                      size="icon"
                      variant="outline"
                      className="w-9 h-9 bg-background/90 hover:bg-background shadow-sm rounded-full"
                      onClick={async (e) => {
                        e.stopPropagation(); // Prevent lightbox open
                        try {
                          if (navigator.share) {
                            await navigator.share({
                              title: product.name,
                              text: `${product.name} - ${formatPrice(product.price_per_unit)}`,
                              url: window.location.href
                            });
                            // Track share event
                            if (typeof window !== 'undefined' && window.gtag) {
                              window.gtag('event', 'share', {
                                method: 'web_share_api',
                                content_type: 'product',
                                item_id: product.id
                              });
                            }
                          } else {
                            // Fallback: copy to clipboard
                            await navigator.clipboard.writeText(window.location.href);
                            toast.success('הקישור הועתק ללוח');
                            // Track share event
                            if (typeof window !== 'undefined' && window.gtag) {
                              window.gtag('event', 'share', {
                                method: 'clipboard',
                                content_type: 'product',
                                item_id: product.id
                              });
                            }
                          }
                        } catch (error) {
                          console.error('Error sharing:', error);
                          // Try clipboard fallback
                          try {
                            await navigator.clipboard.writeText(window.location.href);
                            toast.success('הקישור הועתק ללוח');
                          } catch (clipboardError) {
                            toast.error('שגיאה בשיתוף המוצר');
                          }
                        }
                      }}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant={isFavorite(product.id) ? "default" : "outline"}
                      className={`w-9 h-9 shadow-sm rounded-full ${isFavorite(product.id) ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-background/90 hover:bg-background hover:text-red-500'}`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent lightbox open
                        toggleFavorite(product.id, product);
                      }}
                    >
                      <Heart className={`h-4 w-4 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                    </Button>
                  </div>
                </div>
                {isOnSale && (
                  <div className="absolute top-4 right-4 flex flex-col gap-2">
                    <Badge className="bg-destructive text-destructive-foreground">
                      מבצע מוגבל בזמן
                    </Badge>
                    {daysRemaining !== null && daysRemaining <= 7 && (
                      <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                        {daysRemaining === 0 ? 'מסתיים היום!' : `נשארו ${daysRemaining} ימים`}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2 overflow-x-auto">
                {allImages && allImages.length > 0 ? (
                  allImages.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${currentImageIndex === index ? 'border-primary' : 'border-border'
                        }`}
                    >
                      <img
                        src={ImageManager.getImageUrl(image)}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-contain"
                        onError={(e) => ImageManager.handleImageError(e)}
                      />
                      {/* AI Badge for thumbnails */}
                      {index >= (product.images?.length || 0) && (
                        <div className="absolute bottom-1 right-1 bg-purple-600/60 backdrop-blur-sm text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium">
                          AI
                        </div>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
                    <Package className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>

            {/* Lightbox Modal */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
              <DialogContent className="max-w-[90vw] max-h-[85vh] p-0 border-0 overflow-hidden [&>button]:hidden">
                <div className="relative w-full h-full flex items-center justify-center bg-black p-2 md:p-8">
                  {/* Close Button on the left */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 left-2 md:top-4 md:left-4 z-50 text-black bg-white hover:bg-white/90 rounded-full shadow-md"
                    onClick={() => setLightboxOpen(false)}
                  >
                    <X className="h-6 w-6" />
                  </Button>

                  {allImages && allImages.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-50 text-black bg-white hover:bg-white/90 rounded-full shadow-md"
                        onClick={() => {
                          setLightboxImageIndex((prev) =>
                            prev === 0 ? allImages.length - 1 : prev - 1
                          );
                        }}
                      >
                        <ChevronLeft className="h-8 w-8" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-50 text-black bg-white hover:bg-white/90 rounded-full shadow-md"
                        onClick={() => {
                          setLightboxImageIndex((prev) =>
                            prev === allImages.length - 1 ? 0 : prev + 1
                          );
                        }}
                      >
                        <ChevronRight className="h-8 w-8" />
                      </Button>
                    </>
                  )}

                  <img
                    src={ImageManager.getImageUrl(allImages[lightboxImageIndex] || '')}
                    alt={product.name}
                    className="max-w-full max-h-[calc(85vh-4rem)] w-auto h-auto object-contain"
                    onError={(e) => ImageManager.handleImageError(e)}
                  />

                  {/* AI Badge in Lightbox */}
                  {lightboxImageIndex >= (product.images?.length || 0) && (
                    <div className="absolute bottom-4 right-4 z-50">
                      <div
                        className="bg-purple-600/60 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAiTooltip(!showAiTooltip);
                        }}
                      >
                        <Wand2 className="w-4 h-4" />
                        <span className="font-medium">הדמיית AI</span>
                      </div>
                      {showAiTooltip && (
                        <div className="absolute bottom-full right-0 mb-2 w-64 bg-purple-900 text-white text-xs p-3 rounded-lg shadow-xl">
                          <p className="leading-relaxed">
                            תמונה זו נוצרה באמצעות בינה מלאכותית להמחשה בלבד. המרקם והגוון המדויק עשויים להיות שונים במציאות.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {allImages && allImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
                      {lightboxImageIndex + 1} / {allImages.length}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Product Info */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Removed ProductGradeBadge */}
                  {/* Removed category and new badges */}
                  {isOnSale && (
                    <Badge className="bg-destructive text-destructive-foreground">
                      מבצע מוגבל בזמן
                    </Badge>
                  )}

                </div>
              </div>
              {/* Product Title */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <h1 className="text-3xl font-bold text-foreground">{product.name}</h1>

                {!isGroupBuy && !product.liquidation_sale_enabled && (
                  <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-0 shadow-md">
                    מכירה רגילה
                  </Badge>
                )}
              </div>



              {/* Group Buy Section - Show prominently at top */}
              {product.group_buy_enabled && (
                <div className="mb-6">
                  <SimpleGroupBuyDisplay
                    currentQuantity={product.group_buy_current_quantity || 0}
                    targetQuantity={product.group_buy_target_quantity || 100}
                    endDate={product.group_buy_end_date}
                    currentPrice={product.group_buy_locked_price || product.price_per_unit}
                    measurementUnit={product.measurement_unit}
                    deliveryDays={product.group_buy_delivery_days}
                  />
                </div>
              )}

              {/* Liquidation Sale Section */}
              {product.liquidation_sale_enabled && (
                <div className="mb-6">
                  <LiquidationSaleDisplay
                    totalInventory={product.liquidation_total_inventory || 0}
                    currentInventory={product.liquidation_current_inventory || 0}
                    thresholdPercent={product.liquidation_threshold_percent || 80}
                    endDate={product.liquidation_end_date}
                    deliveryDays={product.liquidation_delivery_days}
                    status={product.liquidation_status}
                    measurementUnit={isPackageMode ? 'package' : product.measurement_unit}
                  />
                </div>
              )}


              {/* Quantity Input */}
              {/* Purchase Strip / Quantity Input Details */}
              <Card className="mb-6 overflow-hidden border-muted shadow-md">
                {/* Header: Unit Price & Status */}
                {/* Header: Unit Price & Status */}
                <CardContent className="p-6">
                  {isOnSale && (
                    <div className="flex justify-center mb-4">
                      <Badge variant="outline" className="text-red-500 border-red-500 animate-pulse bg-red-50">
                        בתוקף עד {new Date(product.sale_end!).toLocaleDateString('he-IL')}
                      </Badge>
                    </div>
                  )}

                  {isPackageMode && product.package_coverage_per_unit ? (
                    /* --- PACKAGE MODE UI --- */
                    <div className="flex flex-col gap-6 items-stretch">

                      {/* 1. Header Row (Package Info) - Top based on mockup */}
                      <div className="flex flex-row items-center justify-between px-2 pt-2 text-sm text-foreground">
                        <div className="flex flex-col items-center flex-1">
                          <span className="text-muted-foreground text-xs mb-1">תכולת חבילה</span>
                          <span className="font-bold text-base">{product.package_coverage_per_unit} {getPackageBaseUnit(product.measurement_unit)}</span>
                        </div>
                        <div className="w-px h-8 bg-border mx-2"></div>
                        <div className="flex flex-col items-center flex-1">
                          <span className="text-muted-foreground text-xs mb-1">מחיר ל{getPackageBaseUnit(product.measurement_unit)}</span>
                          <span className="font-bold text-base">{formatPrice(product.price_per_unit)}</span>
                        </div>
                      </div>

                      {/* 3. Package Price Display (NOW PROMINENT) */}
                      <div className="flex flex-row items-center justify-center gap-4 mt-8 mb-6">
                        {/* Discount Badge & Original Price */}
                        {product.original_price && product.original_price > product.price_per_unit && (
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant="destructive" className="h-7 px-2 text-sm font-bold bg-[#E81E25] hover:bg-[#D4101A] text-white border-0 shadow-sm">
                              -{Math.round(((product.original_price - product.price_per_unit) / product.original_price) * 100)}%
                            </Badge>
                            {/* Original Package Price (Approximation) */}
                            <span className="text-sm text-muted-foreground line-through decoration-slate-400">
                              {formatPrice(product.original_price * product.package_coverage_per_unit)}
                            </span>
                          </div>
                        )}

                        {/* Current Package Price */}
                        <div className="flex flex-col items-start">
                          <div className="flex items-baseline gap-1">
                            <span className="text-5xl font-black text-foreground tracking-tight">
                              {formatPrice(product.price_per_unit * product.package_coverage_per_unit).replace('₪', '')}
                            </span>
                            <span className="text-xl font-medium text-foreground">₪</span>
                          </div>
                          <span className="text-sm text-muted-foreground self-center -mt-1">
                            לחבילה/יחידה
                          </span>
                        </div>
                      </div>

                      {/* 3. Quantity Control Section */}
                      <div className="flex-1 w-full flex flex-col items-center gap-3 mt-2">

                        {/* Limits Text - ABOVE INPUTs as requested */}
                        <div className="text-xs text-muted-foreground text-center w-full flex justify-center gap-2">
                          <span>מינימום: {product.min_quantity || 1}</span>
                          <span className="text-border">|</span>
                          <span>מקסימום: {product.max_quantity || 35}</span>
                          <span className="text-border">|</span>
                          <span>כפולות: {product.step_quantity || 1}</span>
                        </div>

                        {/* Input Controls */}
                        <div className="flex items-center gap-3 w-full justify-center">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-12 w-12 rounded-lg border-muted-foreground/20 hover:bg-muted/50"
                            onClick={() => {
                              const stepQty = product.step_quantity || 1;
                              const minQty = product.min_quantity || 1;
                              const currentQty = parseInt(userQuantity) || minQty;
                              if (currentQty <= minQty && isInCart) {
                                setShowRemoveConfirm(true);
                                return;
                              }
                              const newQty = Math.max(minQty, currentQty - stepQty);
                              setUserQuantity(newQty.toString());
                              setQuantity(newQty);
                            }}
                          >
                            <Minus className="h-5 w-5" />
                          </Button>

                          <div className="relative w-40">
                            <Input
                              type="number"
                              value={userQuantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                setUserQuantity(e.target.value);
                                if (!isNaN(val) && val > 0) setQuantity(val);
                              }}
                              onBlur={(e) => {
                                let val = parseInt(e.target.value);
                                const minQty = product.min_quantity || 1;
                                if (isNaN(val) || val < minQty) val = minQty;

                                // Calculate Maximum Allowed Quantity (in Packages)
                                let absoluteMax = product.max_quantity || 35; // Default max packages

                                // Base Unit Inventory (e.g. SQM)
                                const totalInventoryBase = product.total_inventory || 999999;
                                const coverage = product.package_coverage_per_unit || 1;

                                // 1. Check Physical Inventory
                                let maxPackagesByInventory = totalInventoryBase; // Inventory is already in packages

                                // 2. Liquidation Limit
                                if (product.liquidation_sale_enabled) {
                                  const liquidationInv = product.liquidation_current_inventory !== undefined
                                    ? product.liquidation_current_inventory
                                    : totalInventoryBase;
                                  maxPackagesByInventory = liquidationInv; // Inventory is already in packages
                                }

                                // 3. Group Buy Limit
                                const isGroupBuy = product.group_buy_enabled;
                                if (isGroupBuy) {
                                  const target = product.group_buy_target_quantity || product.target_quantity || 999999;
                                  const currentGroupQty = product.group_buy_current_quantity || product.current_quantity || 0;
                                  const remainingToTargetBase = Math.max(0, target - currentGroupQty);

                                  // Limit to target if enabled
                                  if (product.group_buy_limit_to_target && remainingToTargetBase > 0) {
                                    const remainingPackages = remainingToTargetBase; // Target/Current are already in packages
                                    maxPackagesByInventory = Math.min(maxPackagesByInventory, remainingPackages);
                                  }
                                }

                                const maxQty = Math.min(
                                  product.max_quantity || 999999,
                                  maxPackagesByInventory
                                );

                                if (val > maxQty) {
                                  val = maxQty;
                                  toast.error(`מקסימום להזמנה: ${maxQty} חבילות`);
                                }

                                // Enforce step quantity
                                const step = product.step_quantity || 1;
                                if ((val - minQty) % step !== 0) {
                                  val = minQty + Math.ceil((val - minQty) / step) * step;
                                }

                                setQuantity(val);
                                setUserQuantity(val.toString());
                              }}
                              className="h-12 text-center text-2xl font-bold border-muted-foreground/20 bg-transparent focus-visible:ring-lime hover:border-lime/50 transition-colors"
                            />
                          </div>

                          <Button
                            variant="outline"
                            size="icon"
                            className="h-12 w-12 rounded-lg border-muted-foreground/20 hover:bg-muted/50"
                            onClick={() => {
                              const stepQty = product.step_quantity || 1;
                              const currentQty = parseInt(userQuantity) || (product.min_quantity || 1);

                              // Calculate Maximum Allowed Quantity (in Packages)
                              let maxPackagesByInventory = 999999;

                              // Base Unit Inventory (e.g. SQM)
                              const totalInventoryBase = product.total_inventory || 999999;
                              const coverage = product.package_coverage_per_unit || 1;

                              // 1. Check Physical Inventory
                              maxPackagesByInventory = totalInventoryBase; // Inventory is already in packages

                              // 2. Liquidation Limit
                              if (product.liquidation_sale_enabled) {
                                const liquidationInv = product.liquidation_current_inventory !== undefined
                                  ? product.liquidation_current_inventory
                                  : totalInventoryBase;
                                maxPackagesByInventory = liquidationInv; // Inventory is already in packages
                              }

                              // 3. Group Buy Limit
                              const isGroupBuy = product.group_buy_enabled;
                              if (isGroupBuy) {
                                const target = product.group_buy_target_quantity || product.target_quantity || 999999;
                                const currentGroupQty = product.group_buy_current_quantity || product.current_quantity || 0;
                                const remainingToTargetBase = Math.max(0, target - currentGroupQty);

                                // Limit to target if enabled
                                if (product.group_buy_limit_to_target && remainingToTargetBase > 0) {
                                  const remainingPackages = remainingToTargetBase; // Target/Current are already in packages
                                  maxPackagesByInventory = Math.min(maxPackagesByInventory, remainingPackages);
                                }
                              }

                              const maxQty = Math.min(
                                product.max_quantity || 999999,
                                maxPackagesByInventory
                              );

                              if (currentQty >= maxQty) {
                                toast.error(`מקסימום להזמנה: ${maxQty} חבילות`);
                                return;
                              }
                              const newQty = currentQty + stepQty;
                              setUserQuantity(newQty.toString());
                              setQuantity(newQty);
                            }}
                          >
                            <Plus className="h-5 w-5" />
                          </Button>
                        </div>
                        <div className="text-center mt-2 font-medium text-lime-700">
                          תקבל בפועל: {parseFloat((quantity * (product.package_coverage_per_unit || 1)).toFixed(2))} {getPackageBaseUnit(product.measurement_unit)}
                        </div>
                      </div>

                      <div className="w-full h-px bg-border my-2"></div>

                      {/* 4. Total & Action */}
                      <div className="flex-1 w-full flex flex-col gap-4">
                        <div className="flex flex-col items-center justify-center gap-1">
                          <span className="text-sm text-foreground/70 font-medium">לתשלום</span>
                          <div className="text-4xl font-black text-[#5F8C1C] tracking-tight">
                            {formatPrice(quantity * product.package_coverage_per_unit * product.price_per_unit)}
                          </div>
                        </div>

                        <Button
                          size="lg"
                          onClick={() => {
                            if (isInCart) {
                              const cartQtyChanged = (cartItem?.quantity || 0) !== parseInt(userQuantity);
                              if (!cartQtyChanged) {
                                openCart();
                                return;
                              }
                            }
                            handleAddToCart();
                          }}
                          className={`w-full text-lg font-bold h-14 transition-all rounded-xl ${(() => {
                            // Group Buy Exclusion: Always show active color (unless explicitly disabled for other reasons?)
                            // For now, if it's group buy, we don't show "grey" out of stock style.
                            if (product.group_buy_enabled) {
                              if (flashQuoteButton) return 'bg-red-500 hover:bg-red-600 scale-105';
                              if (isInCart && (cartItem?.quantity || 0) === parseInt(userQuantity)) {
                                return 'bg-gray-100 text-gray-800 border-2 border-gray-200 hover:bg-gray-200 shadow-none';
                              }
                              return 'bg-[#7BB02C] hover:bg-[#6A9A24] text-white shadow-lg shadow-[#7BB02C]/20';
                            }

                            const totalBase = product.liquidation_sale_enabled
                              ? (product.liquidation_current_inventory ?? product.total_inventory ?? 0)
                              : (product.total_inventory ?? 0);

                            if (totalBase <= 0) {
                              return 'bg-gray-400 hover:bg-gray-400 text-white cursor-not-allowed';
                            }

                            if (flashQuoteButton) return 'bg-red-500 hover:bg-red-600 scale-105';

                            if (isInCart && (cartItem?.quantity || 0) === parseInt(userQuantity)) {
                              return 'bg-gray-100 text-gray-800 border-2 border-gray-200 hover:bg-gray-200 shadow-none';
                            }

                            return 'bg-[#7BB02C] hover:bg-[#6A9A24] text-white shadow-lg shadow-[#7BB02C]/20';
                          })()
                            }`}
                          disabled={(() => {
                            // Group Buy Exclusion: Never disable for "out of stock" reasons (unless specifically handled elsewhere)
                            if (product.group_buy_enabled) return false;

                            // Disable if inventory < min OR out of stock
                            const totalBase = product.liquidation_sale_enabled
                              ? (product.liquidation_current_inventory ?? product.total_inventory ?? 0)
                              : (product.total_inventory ?? 999999); // 999999 fallback usually means unlimited, but if 0 it means 0.

                            // Note: total_inventory might be 0.
                            const effectiveTotal = product.liquidation_sale_enabled
                              ? (product.liquidation_current_inventory ?? 0)
                              : (product.total_inventory ?? 0);

                            if (effectiveTotal <= 0) return true;

                            const minPackages = product.min_quantity || 1;
                            return totalBase < minPackages;
                          })()}
                        >
                          <div className="flex items-center justify-center gap-2 w-full">
                            {(() => {
                              // Group Buy Exclusion
                              if (product.group_buy_enabled) {
                                if (isInCart) {
                                  return (
                                    <>
                                      <Check className="h-6 w-6" />
                                      <span>{(cartItem?.quantity || 0) !== parseInt(userQuantity) ? 'עדכן כמות' : 'נוסף לעגלה'}</span>
                                    </>
                                  );
                                }
                                return (
                                  <>
                                    <ShoppingBag className="h-6 w-6" />
                                    <span>הוסף לעגלה</span>
                                  </>
                                );
                              }

                              const effectiveTotal = product.liquidation_sale_enabled
                                ? (product.liquidation_current_inventory ?? 0)
                                : (product.total_inventory ?? 0);

                              if (effectiveTotal <= 0) {
                                return <span>אזל מהמלאי</span>;
                              }

                              if (isInCart) {
                                return (
                                  <>
                                    <Check className="h-6 w-6" />
                                    <span>{(cartItem?.quantity || 0) !== parseInt(userQuantity) ? 'עדכן כמות' : 'נוסף לעגלה'}</span>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <ShoppingBag className="h-6 w-6" />
                                  <span>הוסף לעגלה</span>
                                </>
                              );
                            })()}
                          </div>
                          <span className="mr-auto opacity-0 w-6"></span> {/* Spacer for centering text if needed */}
                        </Button>
                      </div>

                    </div>
                  ) : (
                    /* --- NON-PACKAGE MODE (Unified UI) --- */
                    <div className="flex flex-col gap-6 items-stretch">

                      {/* 1. Price Header - Centered */}
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-3xl font-bold text-lime">
                            {formatPrice(product.price_per_unit)}
                          </span>
                          <span className="text-lg text-muted-foreground">
                            / {formatMeasurementUnit(product.measurement_unit)}
                          </span>
                        </div>
                        {product.original_price && product.original_price > product.price_per_unit && (
                          <div className="flex items-center gap-3">
                            <span className="text-base text-muted-foreground line-through">
                              {formatPrice(product.original_price)}
                            </span>
                            <Badge variant="destructive" className="h-6 px-2 text-xs bg-red-500 hover:bg-red-600 text-white border-0">
                              -{Math.round(((product.original_price - product.price_per_unit) / product.original_price) * 100)}%
                            </Badge>
                          </div>
                        )}
                      </div>

                      <ProductActions
                        product={product}
                        quantity={quantity}
                        setQuantity={setQuantity}
                        userQuantity={userQuantity}
                        setUserQuantity={setUserQuantity}
                        isInCart={isInCart}
                        cartItem={cartItem}
                        handleAddToCart={handleAddToCart}
                        flashQuoteButton={flashQuoteButton}
                        isPackageMode={isPackageMode}
                        openCart={openCart}
                      />
                    </div>
                  )}
                  {/* Global Inventory Check for Button Disable (rendered if min > stock) */}
                  {(() => {
                    // Logic to calculate effective Max Inventory (replicated for render check)
                    const totalInventoryBase = product.total_inventory || 0;
                    const minQty = product.min_quantity || 1;

                    let maxByInventory = totalInventoryBase;
                    if (product.liquidation_sale_enabled) {
                      maxByInventory = product.liquidation_current_inventory !== undefined
                        ? product.liquidation_current_inventory
                        : (totalInventoryBase || 0);
                    }

                    // IF inventory is 0, we already display "Out of Stock" in the button itself.
                    // So we do NOT need to display a separate message for 0 inventory.
                    if (maxByInventory <= 0) {
                      return null;
                    }

                    return maxByInventory < minQty ? (
                      <div className="text-center text-destructive font-bold mt-2">
                        המלאי הנותר ({maxByInventory}) נמוך מהכמות המינימלית להזמנה ({minQty}). לא ניתן להזמין.
                      </div>
                    ) : null;
                  })()}
                </CardContent>
              </Card>






              {/* Remove Confirmation Dialog */}
              <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
                <DialogContent className="sm:max-w-md" dir="rtl">
                  <div className="text-center space-y-4">
                    <h3 className="text-lg font-semibold">הסרת מוצר מהעגלה</h3>
                    <p className="text-muted-foreground">האם אתה בטוח שברצונך להסיר את המוצר מהעגלה?</p>
                    <div className="flex gap-3 justify-center">
                      <Button variant="outline" onClick={() => setShowRemoveConfirm(false)}>
                        ביטול
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          removeItem(product.id.toString());
                          setShowRemoveConfirm(false);
                          toast.success('המוצר הוסר מהעגלה');
                          // Reset to minimum quantity
                          const minQty = product.min_quantity || 1;
                          setUserQuantity(minQty.toString());

                          if (isPackageMode && product.package_coverage_per_unit) {
                            // minQty is already packages
                            setQuantity(minQty);
                          } else {
                            setQuantity(minQty);
                          }
                        }}
                      >
                        הסר מהעגלה
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Quote Request Button for Large Quantities - Show when user might need more than available */}
              <div className="mb-6">
                <Button
                  variant="outline"
                  className={`w-full transition-all ${flashQuoteButton ? 'animate-pulse bg-lime/20 border-lime' : ''}`}
                  onClick={() => setShowQuoteForm(!showQuoteForm)}
                >
                  <MessageSquare className="h-4 w-4 ml-2" />
                  רוצה כמות גדולה יותר? בקש הצעת מחיר
                </Button>
              </div>

              {/* Quote Request Form */}
              {showQuoteForm && (
                <Card className="mb-6" dir="rtl" id="quote-form-container">
                  <CardHeader className="text-right">
                    <CardTitle className="text-right" style={{ textAlign: 'right' }}>בקשת הצעת מחיר</CardTitle>
                    <p className="text-sm text-muted-foreground text-right" style={{ textAlign: 'right' }}>
                      מלאו את כל הפרטים למענה מהיר יותר
                    </p>
                  </CardHeader>
                  <CardContent dir="rtl">
                    <form onSubmit={handleQuoteSubmit} className="space-y-4" dir="rtl">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-right">
                          <Label htmlFor="requested_quantity" className="text-right block mb-2" style={{ textAlign: 'right' }}>
                            כמות מבוקשת <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="requested_quantity"
                            type="number"
                            value={quoteFormData.requested_quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              setQuoteFormData(prev => ({ ...prev, requested_quantity: value }));
                            }}
                            onBlur={(e) => {
                              // On blur, validate the final value
                              const value = parseInt(e.target.value);
                              const minRequired = (product.max_quantity || product.total_inventory) + 1;
                              if (value && value < minRequired) {
                                setQuoteFormData(prev => ({ ...prev, requested_quantity: minRequired.toString() }));
                              }
                            }}
                            placeholder={`יותר מ-${product.max_quantity || product.total_inventory}`}
                            required
                            className="text-right"
                            dir="rtl"
                            style={{ textAlign: 'right', direction: 'rtl' }}
                          />
                          <p className="text-xs text-muted-foreground mt-1 text-right">
                            יש להזין כמות גדולה מ-{product.max_quantity || product.total_inventory}
                          </p>
                        </div>
                        <div className="text-right">
                          <Label htmlFor="quote_email" className="text-right block mb-2" style={{ textAlign: 'right' }}>
                            אימייל <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="quote_email"
                            type="email"
                            value={quoteFormData.email}
                            onChange={(e) => setQuoteFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="your@email.com"
                            required
                            className="text-right"
                            dir="rtl"
                            style={{ textAlign: 'right', direction: 'rtl' }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <Label htmlFor="quote_phone" className="text-right block mb-2" style={{ textAlign: 'right' }}>טלפון</Label>
                        <Input
                          id="quote_phone"
                          type="tel"
                          value={quoteFormData.phone}
                          onChange={(e) => setQuoteFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="05x-xxxxxxx"
                          className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                          dir="rtl"
                          style={{ textAlign: 'right', direction: 'rtl' }}
                        />
                      </div>
                      <div className="text-right">
                        <Label htmlFor="quote_message" className="text-right block mb-2" style={{ textAlign: 'right' }}>הודעה נוספת</Label>
                        <Textarea
                          id="quote_message"
                          value={quoteFormData.message}
                          onChange={(e) => setQuoteFormData(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="פרטים נוספים על הפרויקט, לוחות זמנים וכו'..."
                          className="text-right min-h-20"
                          dir="rtl"
                          style={{ textAlign: 'right', direction: 'rtl' }}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={() => setShowQuoteForm(false)}>
                          ביטול
                        </Button>
                        <Button type="submit" disabled={quoteLoading} className="flex-row-reverse">
                          <ArrowLeft className="h-4 w-4 ml-2" />
                          {quoteLoading ? 'שולח...' : 'שלח בקשה'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}


            </div>
          </div>

          {/* Product Details - Stacked Layout */}
          <div className="space-y-6 mb-12" dir="rtl">
            {/* Description Section */}
            {product.description && (
              <Card>
                <CardHeader className="flex justify-end">
                  <CardTitle>תיאור</CardTitle>
                </CardHeader>
                <CardContent dir="rtl" className="text-right">
                  <div className="text-right" dir="rtl">
                    <p className="text-muted-foreground leading-relaxed text-right" dir="rtl" style={{ textAlign: 'right', direction: 'rtl' }}>
                      {product.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Specifications Section */}
            <Card>
              <CardHeader className="flex justify-end">
                <CardTitle>מפרט</CardTitle>
              </CardHeader>
              <CardContent dir="rtl" className="text-right">
                <div className="space-y-3" dir="rtl">
                  <div className="flex items-center py-2 border-b border-border">
                    <span className="font-medium">קטגוריה:</span>
                    <span className="text-muted-foreground mr-2">{product.category_name || product.categories?.name || 'לא צוין'}</span>
                  </div>

                  {(product.dimension_width || product.dimension_height || product.dimension_depth) && (
                    <div className="flex items-start py-2 border-b border-border">
                      <span className="font-medium ml-2 shrink-0">מידות:</span>
                      <div className="flex flex-col gap-1 text-muted-foreground">
                        {/* Height -> Length (Aroch) */}
                        {product.dimension_height && (() => {
                          const unit = (product as any).dimension_height_unit || product.dimension_unit || 'cm';
                          return (
                            <div className="flex items-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-border ml-2 mt-0.5"></span>
                              <span>אורך {product.dimension_height} {formatDimensionUnit(unit)}</span>
                            </div>
                          );
                        })()}

                        {/* Width (Rochav) */}
                        {product.dimension_width && (() => {
                          const unit = (product as any).dimension_width_unit || product.dimension_unit || 'cm';
                          return (
                            <div className="flex items-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-border ml-2 mt-0.5"></span>
                              <span>רוחב {product.dimension_width} {formatDimensionUnit(unit)}</span>
                            </div>
                          );
                        })()}

                        {/* Depth -> Thickness (Ovi) - Standardize to mm */}
                        {product.dimension_depth && (() => {
                          let depth = product.dimension_depth;
                          let unit = (product as any).dimension_depth_unit || product.dimension_unit || 'mm';

                          if (unit === 'cm') {
                            depth = parseFloat((depth * 10).toFixed(2));
                            unit = 'mm';
                          } else if (unit === 'm') {
                            depth = parseFloat((depth * 1000).toFixed(2));
                            unit = 'mm';
                          }

                          return (
                            <div className="flex items-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-border ml-2 mt-0.5"></span>
                              <span>עובי {depth} {formatDimensionUnit(unit)}</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {product.country_of_origin && (
                    <div className="flex items-center py-2 border-b border-border">
                      <span className="font-medium">ארץ ייצור:</span>
                      <span className="text-muted-foreground mr-2">{product.country_of_origin}</span>
                    </div>
                  )}

                  {product.material && (
                    <div className="flex items-center py-2 border-b border-border">
                      <span className="font-medium">חומר:</span>
                      <span className="text-muted-foreground mr-2">{product.material}</span>
                    </div>
                  )}

                  {product.finish && (
                    <div className="flex items-center py-2 border-b border-border">
                      <span className="font-medium">גימור:</span>
                      <span className="text-muted-foreground mr-2">{product.finish}</span>
                    </div>
                  )}
                  {/* Specifications items removed as requested */}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grade Legend */}
          <div className="mt-8" id="grade-legend-section">
            <GradeLegend mode="carousel" defaultGrade={product.grade} />
          </div>

          {/* Similar Products */}
          {product.category_id ? (
            <SimilarProducts
              currentProductId={product.id}
              categoryId={product.category_id}
              subcategoryId={product.subcategory_id}
            />
          ) : (
            (() => {
              console.log('ProductDetail: Missing category_id, SimilarProducts will not render', { product_id: product.id, category_id: product.category_id });
              return null;
            })()
          )}
        </div >
      </main >

      <Footer />
    </div >
  );
};

export default ProductDetail;